import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, LineChart, Trash2, Upload, X } from "lucide-react";
import { Fragment, useRef, useState } from "react";

import {
  borrarDiaPronosticoLuxem,
  cargarPronosticosLuxem,
  getPronosticosLuxemDia,
  getPronosticosLuxemResumen,
  previaPronosticosLuxem,
  type PronLuxDia,
  type PronLuxHoja,
  type PronLuxPrevia,
} from "@/lib/api";

const num = (n: number | null | undefined, dec = 3) =>
  n == null ? "—" : n.toLocaleString("es-MX", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  });

const fechaCorta = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });

/*
 * Pronósticos Luxem: carga del Excel de demanda horaria que manda Luxem (una
 * pestaña por día, columnas FechaOperacion | HoraOperacion | Carga 1 … Carga N).
 *
 * El flujo es en dos pasos a propósito: el archivo se sube primero a /previa,
 * que solo parsea y devuelve lo detectado en todas las pestañas, y el usuario
 * confirma antes de que se escriba nada. El mismo File se reenvía a /cargar;
 * no se guarda estado en el servidor entre los dos pasos.
 *
 * En BD los datos quedan NORMALIZADOS (una fila por fecha+hora+carga), que es
 * la vista por omisión de la previa; el toggle "Matriz" los muestra como en el
 * Excel, solo para cotejar a ojo contra el archivo original.
 */
export function PronosticosLuxemPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<PronLuxPrevia | null>(null);
  const [vista, setVista] = useState<"normalizada" | "matriz">("normalizada");
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["pronosticos-luxem-resumen"],
    queryFn: getPronosticosLuxemResumen,
  });
  const dias = q.data?.items ?? [];

  const limpiar = () => {
    setPrevia(null);
    setArchivo(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const verPrevia = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData();
      fd.append("archivo", f);
      return previaPronosticosLuxem(fd);
    },
    onSuccess: (p) => {
      setError(null);
      setAviso(null);
      setPrevia(p);
    },
    onError: (e) => {
      setPrevia(null);
      setError(e instanceof Error ? e.message : "No se pudo leer el archivo");
    },
  });

  const guardar = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData();
      fd.append("archivo", f);
      return cargarPronosticosLuxem(fd);
    },
    onSuccess: (r) => {
      setError(null);
      setAviso(
        `${r.guardadas} pronósticos guardados · ${r.dias.length} día(s): ${r.dias.join(", ")}` +
          ` · cargas ${r.cargas.join(", ")}` +
          (r.hojas_ignoradas.length ? ` · pestañas ignoradas: ${r.hojas_ignoradas.join(", ")}` : ""),
      );
      limpiar();
      qc.invalidateQueries({ queryKey: ["pronosticos-luxem-resumen"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo guardar"),
  });

  const borrar = useMutation({
    mutationFn: (fecha: string) => borrarDiaPronosticoLuxem(fecha),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pronosticos-luxem-resumen"] }),
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar"),
  });

  const onArchivo = (f: File | null) => {
    if (!f) return;
    setArchivo(f);
    verPrevia.mutate(f);
  };

  const hojasValidas = previa?.hojas.filter((h) => !h.ignorada) ?? [];
  const hojasIgnoradas = previa?.hojas.filter((h) => h.ignorada) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <LineChart className="w-5 h-5 text-slate-500" /> Pronósticos Luxem
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Pronóstico de demanda horaria que manda Luxem: <b>una pestaña por día</b> con las
            columnas <b>FechaOperacion | HoraOperacion | Carga 1 | Carga 2 | … | Carga N</b>.
            Al subir el archivo se muestra una <b>previa de todas las pestañas</b> y nada se
            guarda hasta que confirmes. Cada <i>Carga N</i> se guarda como una fila propia
            (columnas <code>carga</code> y <code>pronostico</code>), así que no importa cuántas
            cargas traiga el archivo. Volver a subir un día lo <b>reemplaza</b>; los demás días
            quedan intactos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={(e) => onArchivo(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={verPrevia.isPending || guardar.isPending}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {verPrevia.isPending ? "Leyendo…" : "Cargar Excel"}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}
      {aviso && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">
          {aviso}
        </div>
      )}

      {/* ---------------- Previa: lo detectado en TODAS las pestañas ---------- */}
      {previa && (
        <div className="border border-blue-200 bg-blue-50/40 rounded-lg">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-blue-200">
            <div className="text-sm text-slate-700">
              <div className="font-semibold text-slate-900">
                Previa de «{previa.archivo}»
              </div>
              <div className="text-slate-600 mt-0.5">
                {hojasValidas.length} pestaña(s) · {previa.fechas.length} día(s) ·
                {" "}cargas {previa.cargas.join(", ")} ·{" "}
                <b>{previa.total_filas.toLocaleString("es-MX")}</b> filas a guardar
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded border border-slate-300 overflow-hidden text-xs">
                {(["normalizada", "matriz"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVista(v)}
                    className={`px-2.5 py-1.5 ${
                      vista === v ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {v === "normalizada" ? "Como se guarda" : "Como el Excel"}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => archivo && guardar.mutate(archivo)}
                disabled={guardar.isPending || previa.total_filas === 0}
                className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {guardar.isPending ? "Guardando…" : "Confirmar y guardar"}
              </button>
              <button
                type="button"
                onClick={limpiar}
                className="inline-flex items-center gap-1 border bg-white text-slate-600 rounded px-3 py-2 text-sm hover:bg-slate-50"
              >
                <X className="w-4 h-4" /> Descartar
              </button>
            </div>
          </div>

          {previa.dias_existentes > 0 && (
            <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
              {previa.dias_existentes} de los {previa.fechas.length} día(s) de este archivo
              <b> ya están cargados</b>: al guardar se reemplazan sus pronósticos.
            </div>
          )}
          {hojasIgnoradas.length > 0 && (
            <div className="mx-4 mt-3 bg-slate-100 border border-slate-200 text-slate-600 rounded p-3 text-sm">
              Pestañas ignoradas (no traen el encabezado esperado):{" "}
              {hojasIgnoradas.map((h) => `${h.hoja} (${h.motivo})`).join(" · ")}
            </div>
          )}

          <div className="p-4 space-y-4">
            {hojasValidas.map((h) => (
              <HojaPrevia key={h.hoja} hoja={h} vista={vista} />
            ))}
          </div>
        </div>
      )}

      {/* ---------------- Lo que ya está cargado --------------------------- */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Pronósticos cargados</h2>
        {q.isLoading && (
          <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
            Cargando…
          </div>
        )}
        {q.isError && (
          <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
            {(q.error as Error).message}
          </div>
        )}
        {!q.isLoading && !q.isError && dias.length === 0 && (
          <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
            Todavía no hay pronósticos cargados — sube el Excel de Luxem para empezar.
          </div>
        )}
        {dias.length > 0 && (
          <div className="bg-white border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left">
                <tr>
                  <th className="px-2 py-2 w-6" />
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2 text-right">Horas</th>
                  <th className="px-3 py-2">Cargas</th>
                  <th className="px-3 py-2 text-right">Filas</th>
                  <th className="px-3 py-2 text-right">Suma del día</th>
                  <th className="px-3 py-2">Archivo</th>
                  <th className="px-3 py-2">Cargado</th>
                  <th className="px-3 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {dias.map((d: PronLuxDia) => (
                  <Fragment key={d.fecha}>
                  <tr className="border-t hover:bg-slate-50 cursor-pointer"
                    title="Ver el detalle por hora"
                    onClick={() => setAbierto(abierto === d.fecha ? null : d.fecha)}>
                    <td className="px-2 py-1.5 text-slate-400">
                      {abierto === d.fecha
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">{fechaCorta(d.fecha)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{d.horas}</td>
                    <td className="px-3 py-1.5">{d.cargas.join(", ")}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{d.filas}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(d.suma)}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500 max-w-xs truncate"
                      title={d.archivo ?? ""}>
                      {d.archivo ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">
                      {new Date(d.cargado_en).toLocaleString("es-MX", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                      {d.cargado_por ? ` · ${d.cargado_por}` : ""}
                    </td>
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        title="Borrar los pronósticos de este día"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(
                            `¿Borrar los ${d.filas} pronósticos del ${d.fecha}?`)) {
                            borrar.mutate(d.fecha);
                          }
                        }}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  {abierto === d.fecha && (
                    <tr className="border-t bg-slate-50/60">
                      <td colSpan={9} className="px-4 py-3">
                        <DetalleDia fecha={d.fecha} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            <div className="px-3 py-2 border-t text-xs text-slate-500">
              {dias.length} día(s) cargado(s) ·{" "}
              {dias.reduce((a, d) => a + d.filas, 0).toLocaleString("es-MX")} pronósticos en total
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Una pestaña dentro de la previa: resumen por carga + el detalle. */
function HojaPrevia({ hoja, vista }: { hoja: PronLuxHoja; vista: "normalizada" | "matriz" }) {
  const cargas = hoja.cargas.map((c) => c.carga);
  // Vista "como el Excel": las filas del detalle re-pivotadas a fecha+hora.
  const matriz = new Map<string, Record<number, number>>();
  if (vista === "matriz") {
    for (const d of hoja.detalle) {
      const k = `${d.fecha}|${String(d.hora).padStart(2, "0")}`;
      const fila = matriz.get(k) ?? {};
      fila[d.carga] = d.pronostico;
      matriz.set(k, fila);
    }
  }

  return (
    <div className="bg-white border rounded-lg">
      <div className="px-3 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="font-semibold text-slate-800">Pestaña «{hoja.hoja}»</span>
        <span className="text-slate-600">
          {hoja.fechas.map(fechaCorta).join(", ")} · {hoja.horas} hora(s) ·{" "}
          {hoja.cargas.length} carga(s) · {hoja.filas} filas
        </span>
      </div>

      <div className="px-3 py-2 border-b flex flex-wrap gap-2">
        {hoja.cargas.map((c) => (
          <div key={c.carga} className="border rounded px-3 py-1.5 text-xs bg-slate-50">
            <div className="font-semibold text-slate-700">Carga {c.carga}</div>
            <div className="text-slate-500 tabular-nums">
              suma {num(c.suma)} · prom {num(c.promedio)} · mín {num(c.minimo)} · máx{" "}
              {num(c.maximo)} · {c.horas} h
            </div>
          </div>
        ))}
      </div>

      <div className="max-h-80 overflow-auto">
        {vista === "normalizada" ? (
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
              <tr>
                <th className="px-3 py-1.5">fecha</th>
                <th className="px-3 py-1.5 text-right">hora</th>
                <th className="px-3 py-1.5 text-right">carga</th>
                <th className="px-3 py-1.5 text-right">pronostico</th>
              </tr>
            </thead>
            <tbody>
              {hoja.detalle.map((d, i) => (
                <tr key={`${d.fecha}-${d.hora}-${d.carga}`}
                  className={i % 2 ? "bg-slate-50/60" : undefined}>
                  <td className="px-3 py-1 tabular-nums">{d.fecha}</td>
                  <td className="px-3 py-1 text-right tabular-nums">{d.hora}</td>
                  <td className="px-3 py-1 text-right tabular-nums">{d.carga}</td>
                  <td className="px-3 py-1 text-right tabular-nums">{num(d.pronostico)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
              <tr>
                <th className="px-3 py-1.5">FechaOperacion</th>
                <th className="px-3 py-1.5 text-right">HoraOperacion</th>
                {cargas.map((c) => (
                  <th key={c} className="px-3 py-1.5 text-right">Carga {c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...matriz.entries()].sort(([a], [b]) => a.localeCompare(b)).map(
                ([k, fila], i) => {
                  const [f, h] = k.split("|");
                  return (
                    <tr key={k} className={i % 2 ? "bg-slate-50/60" : undefined}>
                      <td className="px-3 py-1 tabular-nums">{f}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{Number(h)}</td>
                      {cargas.map((c) => (
                        <td key={c} className="px-3 py-1 text-right tabular-nums">
                          {fila[c] == null ? "—" : num(fila[c])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Detalle de un día ya guardado: horas × cargas, como el Excel, con totales. */
function DetalleDia({ fecha }: { fecha: string }) {
  const q = useQuery({
    queryKey: ["pronosticos-luxem-dia", fecha],
    queryFn: () => getPronosticosLuxemDia(fecha),
  });
  if (q.isLoading) return <div className="text-sm text-slate-500">Cargando…</div>;
  if (q.isError) return <div className="text-sm text-red-600">{(q.error as Error).message}</div>;

  const items = q.data?.items ?? [];
  const cargas = [...new Set(items.map((i) => i.carga))].sort((a, b) => a - b);
  const horas = [...new Set(items.map((i) => i.hora))].sort((a, b) => a - b);
  const valor = new Map(items.map((i) => [`${i.hora}|${i.carga}`, i.pronostico]));
  const suma = (c: number) =>
    items.filter((i) => i.carga === c).reduce((a, i) => a + i.pronostico, 0);

  return (
    <div className="bg-white border rounded-lg max-h-96 overflow-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
          <tr>
            <th className="px-3 py-1.5 text-right w-20">Hora</th>
            {cargas.map((c) => (
              <th key={c} className="px-3 py-1.5 text-right">Carga {c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {horas.map((h, i) => (
            <tr key={h} className={i % 2 ? "bg-slate-50/60" : undefined}>
              <td className="px-3 py-1 text-right tabular-nums">{h}</td>
              {cargas.map((c) => (
                <td key={c} className="px-3 py-1 text-right tabular-nums">
                  {num(valor.get(`${h}|${c}`))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-100 font-semibold text-slate-700 sticky bottom-0">
          <tr>
            <td className="px-3 py-1.5 text-right">Suma</td>
            {cargas.map((c) => (
              <td key={c} className="px-3 py-1.5 text-right tabular-nums">{num(suma(c))}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
